import React from 'react';
import EnhancedTooltip from './EnhancedTooltip';
import PropTypes from 'prop-types';

/**
 * FinancialDataTooltip - A specialized tooltip for financial data with common formatting
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The element to attach the tooltip to
 * @param {string} props.fieldName - The name of the financial field
 * @param {string} props.description - The description/explanation of the field
 * @param {string|number} props.previousValue - Previous value for comparison
 * @param {string} props.trend - Trend direction ('positive', 'negative', or null)
 * @param {number} props.sectorRanking - Ranking within the sector
 * @param {number} props.totalInSector - Total number of companies in the sector
 * @param {string} props.sectorName - Name of the sector
 * @param {number} props.toAverage - Percentage difference from sector average
 * @param {string|number} props.sectorAverage - Sector average value
 * @param {string} props.source - Data source information
 * @param {string} props.placement - Tooltip placement
 * @param {Object} props.style - Additional styles for the trigger element
 */
const FinancialDataTooltip = ({
  children,
  fieldName,
  description,
  previousValue,
  trend,
  sectorRanking,
  totalInSector,
  sectorName,
  toAverage,
  sectorAverage,
  source,
  placement = 'top',
  style = {},
  ...otherProps
}) => {
  const renderTooltipContent = () => {
    return (
      <div className="tooltip-content">
        {/* Field name as header */}
        <strong>{fieldName}</strong>
        
        {/* Description */}
        {description && (
          <div className="neutral">{description}</div>
        )}
        
        {/* Previous value */}
        {previousValue !== null && previousValue !== undefined && (
          <div>
            Previous value: <span className="highlight">{previousValue}</span>
          </div>
        )}
        
        {/* Trend information */}
        {trend && (
          <div>
            Trend: <span className={trend === 'positive' ? 'trend-positive' : 'trend-negative'}>
              {trend}
            </span>
          </div>
        )}
        
        {/* Sector ranking with percentile - DAS-1928 */}
        {sectorRanking !== null && sectorRanking !== undefined && totalInSector && (
          <div>
            Sector ranking: <span className="highlight">{sectorRanking}</span> of {totalInSector}
            {sectorName && ` (${sectorName})`}
            {' '}
            <span className="highlight" style={{ marginLeft: '4px' }}>
              ({Math.round(((totalInSector - sectorRanking) / totalInSector) * 100)}th percentile)
            </span>
          </div>
        )}
        
        {/* Comparison to sector average */}
        {toAverage !== null && toAverage !== undefined && sectorAverage && (
          <div>
            vs. sector average: <span className={toAverage > 0 ? 'trend-positive' : 'trend-negative'}>
              {toAverage > 0 ? '+' : ''}{toAverage}% ({sectorAverage})
            </span>
          </div>
        )}
        
        {/* Source information */}
        {source && (
          <div className="source-info">
            Source: {source}
          </div>
        )}
      </div>
    );
  };

  return (
    <EnhancedTooltip
      title={renderTooltipContent()}
      placement={placement}
      style={style}
      {...otherProps}
    >
      {children}
    </EnhancedTooltip>
  );
};

FinancialDataTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  fieldName: PropTypes.string.isRequired,
  description: PropTypes.string,
  previousValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trend: PropTypes.oneOf(['positive', 'negative']),
  sectorRanking: PropTypes.number,
  totalInSector: PropTypes.number,
  sectorName: PropTypes.string,
  toAverage: PropTypes.number,
  sectorAverage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  source: PropTypes.string,
  placement: PropTypes.string,
  style: PropTypes.object,
};

export default FinancialDataTooltip;
